import { useGlobalSearchParams, useLocalSearchParams } from 'expo-router'
import { InsightTrendId, StorageKey, StorageStore, useStore } from 'zens-store'
import {
  AllWeekdays,
  CalendarValue,
  TablePagination,
  TableSort,
  Weekday,
  ZoomValue,
  defaultDatePreset,
  parseCalendarValue,
} from 'zens-storybook'
import { format } from 'date-fns'

import { useRouter } from './provider'
import Router from './router'

type RouterHook<T> = [T, (value: T) => void]

export function useParams<T = string>(): RouterHook<Record<string, T>> {
  const router = useRouter()
  const globalParams = useGlobalSearchParams() as Record<string, T>
  const localParams = useLocalSearchParams() as Record<string, T>
  return [{ ...localParams, ...globalParams }, router.setParams]
}

export function useStringParams<T = string>(): RouterHook<Record<string, T>> {
  const router = useRouter()
  return [
    useParsedParams<T>((value: string) => {
      if (value === 'undefined') return undefined as T
      return value as T
    }),
    setStringParams<T>(router),
  ]
}

function setStringParams<T = string>(router: Router) {
  return (params: Record<string, T | undefined>) =>
    router.setParams(Object.keys(params).reduce((acc, key) => ({ ...acc, [key]: `${params[key]}` }), {}))
}

export function useNumberParams(): RouterHook<Record<string, number>> {
  const router = useRouter()
  return [
    useParsedParams((value) => {
      const parsed = parseInt(value, 10)
      if (isNaN(parsed)) return 0
      return parsed
    }),
    setNumberParams(router),
  ]
}

function setNumberParams(router: Router) {
  return (params: Record<string, number | undefined>) =>
    router.setParams(Object.keys(params).reduce((acc, key) => ({ ...acc, [key]: `${params[key]}` }), {}))
}

export function useBooleanParams(): RouterHook<Record<string, boolean>> {
  const router = useRouter()
  return [useParsedParams((value) => value !== 'false'), setBooleanParams(router)]
}

function setBooleanParams(router: Router) {
  return (params: Record<string, boolean>) =>
    router.setParams(Object.keys(params).reduce((acc, key) => ({ ...acc, [key]: params[key] ? 'true' : 'false' }), {}))
}

export function useWeekdaysParam(): RouterHook<Weekday[]> {
  const router = useRouter()
  const { storage } = useStore()
  const [params] = useParams()
  const weekdays = params?.weekdays
  if (weekdays === undefined) return [weekdaysDefault(storage), setWeekdaysParam(router)]
  return [weekdays.split(',') as Weekday[], setWeekdaysParam(router)]
}

function setWeekdaysParam(router: Router) {
  return (value: Weekday[]) => router.setParams({ weekdays: (value?.length > 0 ? value : AllWeekdays).join(',') })
}

export function useWorkingHoursParam(): RouterHook<boolean> {
  const router = useRouter()
  const { storage } = useStore()
  const [params] = useParams()
  const workingHours = params?.workingHours
  if (workingHours === undefined)
    return [booleanDefault(storage, StorageKey.InsightsWorkingHours), setWorkingHoursParam(router)]
  return [workingHours !== 'false', setWorkingHoursParam(router)]
}

function setWorkingHoursParam(router: Router) {
  return (value: boolean) => router.setParams({ workingHours: value ? 'true' : 'false' })
}

function booleanDefault(storage: StorageStore, key: string) {
  const value = storage.get(key, { session: true })
  return value !== 'false'
}

function weekdaysDefault(storage: StorageStore) {
  const value = storage.get(StorageKey.InsightsWeekdays, { session: true })
  return (value?.split(',') || ['mon', 'tue', 'wed', 'thu', 'fri']) as Weekday[]
}

export function usePeriodParam(): RouterHook<CalendarValue> {
  const router = useRouter()
  const { storage } = useStore()
  const [params] = useParams()
  const { from, to } = params
  if (!from || from === 'undefined') {
    return [periodDefault(storage), setPeriodParam(router)]
  }
  if (!to || to === 'undefined') return [[parseDateValue(from)], setPeriodParam(router)]
  return [[parseDateValue(from), parseDateValue(to)], setPeriodParam(router)]
}

function setPeriodParam(router: Router) {
  return (value: CalendarValue) => {
    const [from, to] = value
    const params = {
      from: format(from, 'yyyy-MM-dd'),
      to: to ? format(to, 'yyy-MM-dd') : undefined,
    }
    router.setParams(params)
  }
}

function periodDefault(storage: StorageStore): CalendarValue {
  const data = storage.get(StorageKey.InsightsPeriod, { session: true })
  const value = parseCalendarValue(data || undefined)
  const [from, to] = value || []
  if (!from) return defaultDatePreset().value as CalendarValue
  if (!to) return [from]
  return [from, to]
}

export function useTrendParam(): RouterHook<InsightTrendId> {
  const router = useRouter()
  const [params] = useParams()
  const trendId = params?.trendId
  if (!trendId || trendId === 'undefined') return [undefined as any, setTrendParam(router)]
  return [trendId as InsightTrendId, setTrendParam(router)]
}

function setTrendParam(router: Router) {
  return (value: string) => router.setParams({ trendId: value as InsightTrendId })
}

export function useSortParam(defaultValue?: TableSort): RouterHook<TableSort> {
  const router = useRouter()
  const [params] = useParams()
  const sort = params?.sort
  if (!sort || sort === 'undefined') return [defaultValue as TableSort, setSortParam(router)]
  const [field, direction] = sort?.split(',')
  return [{ field, direction } as TableSort, setSortParam(router)]
}

function setSortParam(router: Router) {
  return (value: TableSort) => {
    return router.setParams({ sort: `${value.field},${value.direction}` })
  }
}

export function useFilterParam<T = Record<string, number[] | string | boolean>>(defaultValue?: T): RouterHook<T> {
  const router = useRouter()
  const [params] = useParams()
  const filter = params?.filter
  if (!filter || filter === 'undefined' || !Object.keys(filter).length) {
    // If filter is an array, it means it's a legacy filter and should be ignored
    if (Array.isArray(defaultValue)) defaultValue = undefined
    return [defaultValue || ({} as T), setFilterParam(router)]
  }
  return [JSON.parse(filter) as T, setFilterParam(router)]
}

function setFilterParam<T = Record<string, number[] | string | boolean>>(router: Router) {
  return (filter: T) => {
    return router.setParams({ filter: JSON.stringify(filter) })
  }
}

export function useZoomParam(): RouterHook<ZoomValue> {
  const router = useRouter()
  const [params] = useParams()
  const zoom = params?.zoom
  if (!zoom || zoom === 'undefined') return [undefined as any, setZoomParam(router)]
  const [scale, x, y] = zoom?.split(',')
  const zoomValue: ZoomValue = {
    scale: parseFloat(scale),
    x: parseFloat(x),
    y: parseFloat(y),
  }
  return [zoomValue, setZoomParam(router)]
}

function setZoomParam(router: Router) {
  return (value: ZoomValue) => {
    const zoom = `${value.scale},${value.x},${value.y}`
    return router.setParams({ zoom })
  }
}

export function usePaginationParam(): RouterHook<TablePagination> {
  const router = useRouter()
  const [params] = useParams()
  const pagination = params?.pagination
  if (!pagination || pagination === 'undefined') return [{ page: 0, size: 10 }, setPaginationParam(router)]
  const [page, size] = pagination?.split(',')
  return [{ page: parseInt(page, 10) || 0, size: parseInt(size, 10) || 10 }, setPaginationParam(router)]
}

function setPaginationParam(router: Router) {
  return (value: TablePagination) => {
    const pagination = `${value.page},${value.size}`
    return router.setParams({ pagination })
  }
}

export function useArrayParams<T = string>(): RouterHook<Record<string, T[]>> {
  const router = useRouter()
  return [
    useParsedParams<T[]>((value: string) => {
      if (value == null || value === 'undefined') return undefined as any
      return value.split(',') as T[]
    }),
    setArrayParams<T>(router),
  ]
}

function setArrayParams<T>(router: Router) {
  return (params: Record<string, T[] | undefined>) =>
    router.setParams(Object.keys(params).reduce((acc, key) => ({ ...acc, [key]: params[key]?.join(',') }), {}))
}

function useParsedParams<T>(parser: (value: string) => T) {
  const [params] = useParams()
  return Object.keys(params).reduce(
    (acc, key) => {
      return {
        ...acc,
        [key]: parser(params[key]) as T,
      }
    },
    {} as Record<string, T>,
  )
}

function parseDateValue(value: string) {
  return new Date(value)
}
